import { useRef, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import type { TourStep } from './tourSteps'
import { useTourStore } from './useTourStore'

interface Props {
  step: TourStep
  stepIndex: number
  total: number
  /** null when target element hasn't been found yet (fallback: bottom-center) */
  targetRect: DOMRect | null
}

// ─── Arrow ────────────────────────────────────────────────────────────────────

/** Returns the inline style for a small diamond arrow pointing toward the target. */
function arrowStyle(placement: TourStep['placement'], resolvedPlacement: ResolvedPlacement): React.CSSProperties {
  const p = placement === 'auto' ? resolvedPlacement : placement
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 12,
    height: 12,
    // Match the card background (works in light + dark via CSS custom property)
    background: 'var(--c-bg, #FFFDF7)',
  }
  switch (p) {
    case 'bottom':
      return { ...base, top: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)', borderTop: '2px solid var(--c-edge, #0F0E0A)', borderLeft: '2px solid var(--c-edge, #0F0E0A)' }
    case 'top':
      return { ...base, bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)', borderBottom: '2px solid var(--c-edge, #0F0E0A)', borderRight: '2px solid var(--c-edge, #0F0E0A)' }
    case 'right':
      return { ...base, left: -6, top: '50%', transform: 'translateY(-50%) rotate(45deg)', borderLeft: '2px solid var(--c-edge, #0F0E0A)', borderBottom: '2px solid var(--c-edge, #0F0E0A)' }
    case 'left':
    default:
      return { ...base, right: -6, top: '50%', transform: 'translateY(-50%) rotate(45deg)', borderRight: '2px solid var(--c-edge, #0F0E0A)', borderTop: '2px solid var(--c-edge, #0F0E0A)' }
  }
}

// ─── Position computation ─────────────────────────────────────────────────────

type ResolvedPlacement = 'top' | 'bottom' | 'left' | 'right'

function resolveAuto(targetRect: DOMRect): ResolvedPlacement {
  const spaceBelow = window.innerHeight - targetRect.bottom
  const spaceAbove = targetRect.top
  return spaceBelow >= spaceAbove ? 'bottom' : 'top'
}

function computePosition(
  targetRect: DOMRect | null,
  rawPlacement: TourStep['placement'],
  tooltipW: number,
  tooltipH: number,
): { style: React.CSSProperties; resolved: ResolvedPlacement } {
  const gap = 20    // px between spotlight edge and tooltip (accounts for arrow)
  const vw = window.innerWidth
  const vh = window.innerHeight
  const margin = 12 // minimum distance from viewport edge

  // Fallback: no target found yet → bottom-center of screen
  if (!targetRect) {
    const left = Math.max(margin, Math.min(vw / 2 - tooltipW / 2, vw - tooltipW - margin))
    return {
      style: { position: 'fixed', bottom: 24, left, width: tooltipW, zIndex: 9001 },
      resolved: 'bottom',
    }
  }

  const resolved: ResolvedPlacement =
    rawPlacement === 'auto' ? resolveAuto(targetRect) : rawPlacement

  let top: number
  let left: number

  switch (resolved) {
    case 'bottom':
      top = targetRect.bottom + gap
      left = targetRect.left + targetRect.width / 2 - tooltipW / 2
      break
    case 'top':
      top = targetRect.top - tooltipH - gap
      left = targetRect.left + targetRect.width / 2 - tooltipW / 2
      break
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipH / 2
      left = targetRect.right + gap
      break
    case 'left':
    default:
      top = targetRect.top + targetRect.height / 2 - tooltipH / 2
      left = targetRect.left - tooltipW - gap
      break
  }

  // Clamp to viewport
  left = Math.max(margin, Math.min(left, vw - tooltipW - margin))
  top = Math.max(margin, Math.min(top, vh - tooltipH - margin))

  return {
    style: { position: 'fixed', top, left, width: tooltipW, zIndex: 9001 },
    resolved,
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TourTooltip({ step, stepIndex, total, targetRect }: Props) {
  const { next, back, skip, complete } = useTourStore()
  const tooltipRef = useRef<HTMLDivElement>(null)

  const [posStyle, setPosStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 280,
    zIndex: 9001,
    opacity: 0,
    pointerEvents: 'none',
  })
  const [resolved, setResolved] = useState<ResolvedPlacement>('bottom')
  const [visible, setVisible] = useState(false)

  // Measure the tooltip and compute its position before the browser paints
  useLayoutEffect(() => {
    setVisible(false)
    if (!tooltipRef.current) return
    const { width, height } = tooltipRef.current.getBoundingClientRect()
    const { style, resolved: r } = computePosition(
      targetRect,
      step.placement,
      width || 280,
      height || 220,
    )
    setPosStyle({ ...style, opacity: 1 })
    setResolved(r)
    // Small delay so anim-pop fires after position is set
    requestAnimationFrame(() => setVisible(true))
  }, [targetRect, step.placement, stepIndex])

  const isFirst = stepIndex === 0
  const isLast = stepIndex === total - 1

  return createPortal(
    <div
      ref={tooltipRef}
      key={stepIndex}
      className={cn('card-brutal bg-bg p-5 relative', visible && 'anim-pop')}
      style={{ ...posStyle, maxWidth: 300 }}
      role="dialog"
      aria-live="polite"
      aria-label={`Guided tour — step ${stepIndex + 1} of ${total}: ${step.title}`}
    >
      {/* Directional arrow */}
      {targetRect && <div aria-hidden="true" style={arrowStyle(step.placement, resolved)} />}

      {/* Progress indicators */}
      <div className="flex items-center gap-1.5 mb-3">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'rounded-full transition-all duration-200',
              i === stepIndex
                ? 'w-4 h-2 bg-primary'
                : i < stepIndex
                  ? 'w-2 h-2 bg-primary/50'
                  : 'w-2 h-2 bg-muted border border-edge',
            )}
          />
        ))}
        <span className="ml-auto text-xs text-ink-muted font-mono shrink-0">
          {stepIndex + 1} / {total}
        </span>
      </div>

      {/* Content */}
      <h3 className="font-heading font-black text-sm mb-1 leading-tight">
        {step.title}
      </h3>
      <p className="text-xs text-ink-muted leading-relaxed mb-4">
        {step.body}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {!isFirst && (
          <button
            onClick={back}
            className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1 shrink-0"
          >
            <ArrowLeft size={12} weight="bold" /> Back
          </button>
        )}

        <button
          onClick={isLast ? complete : next}
          className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 flex-1 justify-center"
        >
          {isLast
            ? '✓ Got it'
            : (<>Next <ArrowRight size={12} weight="bold" /></>)}
        </button>

        {!isLast && (
          <button
            onClick={skip}
            className="text-xs text-ink-muted hover:text-primary-ink underline underline-offset-2 whitespace-nowrap shrink-0"
          >
            Skip
          </button>
        )}
      </div>
    </div>,
    document.body,
  )
}
